import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import styled, { keyframes, css } from 'styled-components'

const LANGUAGES = [
  { code: 'pt-BR', flag: '🇧🇷', key: 'pt_BR' },
  { code: 'en',   flag: '🇺🇸', key: 'en'   },
  { code: 'es',   flag: '🇦🇷', key: 'es'   },
] as const

const fadeIn = keyframes`
  from { opacity: 0 }
  to   { opacity: 1 }
`

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.97) }
  to   { opacity: 1; transform: translateY(0)    scale(1)    }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10, 15, 30, 0.55);
  backdrop-filter: blur(4px);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.18s ease;
`

const Modal = styled.div`
  background: #1a2035;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 28px 24px 20px;
  width: 320px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
  animation: ${slideUp} 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
`

const Title = styled.h2`
  margin: 0 0 20px;
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.02em;
`

const LanguageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const LangBtn = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${({ $active }) => $active ? 'rgba(99, 179, 237, 0.5)' : 'rgba(255, 255, 255, 0.06)'};
  border-radius: 10px;
  background: ${({ $active }) => $active ? 'rgba(99, 179, 237, 0.1)' : 'rgba(255, 255, 255, 0.03)'};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  ${({ $active }) => !$active && css`
    &:hover {
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 255, 255, 0.12);
    }
  `}
`

const Flag = styled.span`
  font-size: 22px;
  line-height: 1;
  flex-shrink: 0;
`

const LangName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.82);
  flex: 1;
`

const ActiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #63b3ed;
  flex-shrink: 0;
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin: 16px 0 12px;
`

const CloseBtn = styled.button`
  display: block;
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.09);
    color: rgba(255, 255, 255, 0.7);
  }
`

interface LanguageModalProps {
  onClose: () => void
}

export default function LanguageModal({ onClose }: LanguageModalProps) {
  const { t, i18n } = useTranslation()
  const current = i18n.language

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code)
    onClose()
  }

  return createPortal(
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Title>{t('language.modal_title')}</Title>

        <LanguageList>
          {LANGUAGES.map(({ code, flag, key }) => {
            const isActive = current === code || (code === 'pt-BR' && current === 'pt')
            return (
              <LangBtn
                key={code}
                $active={isActive}
                onClick={() => handleSelect(code)}
              >
                <Flag>{flag}</Flag>
                <LangName>{t(`language.${key}`)}</LangName>
                {isActive && <ActiveDot />}
              </LangBtn>
            )
          })}
        </LanguageList>

        <Divider />
        <CloseBtn onClick={onClose}>{t('common.cancel')}</CloseBtn>
      </Modal>
    </Overlay>,
    document.body,
  )
}
